import axios from 'axios';
import DOMPurify from 'dompurify';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 10000,
  withCredentials: false
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Get accessToken from localStorage
    const token = localStorage.getItem('accessToken');
    
    // Add Authorization header if token exists
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    
    // Only sanitize data for XSS, not removing special characters
    if (config.data) {
      config.data = sanitizeObject(config.data);
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      const { status, data } = error.response;
      
      switch (status) {
        case 401:
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
          throw new Error(data.message || 'Session expired. Please login again.');
          
        case 403:
          throw new Error(data.message || 'Access denied.');
          
        case 404:
          throw new Error(data.message || 'Resource not found');
          
        case 409:
          throw new Error(data.message || 'Duplicate entry exists');
          
        case 423:
          throw new Error(data.message || 'Resource is locked');
          
        case 429:
          throw new Error('Too many requests. Please try again later.');
          
        case 500:
          throw new Error('Server error. Please try again later.');
          
        default:
          throw new Error(data.message || 'An error occurred');
      }
    } else if (error.request) {
      throw new Error('Network error. Please check your connection.');
    } else {
      throw new Error('An unexpected error occurred');
    }
  }
);

// Fields that should not be sanitized (preserve special characters)
const NO_SANITIZE_FIELDS = [
  'password',
  'privateKey',
  'host',
  'remotePath',
  'database',
  'instanceName',
  'port',
  'username',
  'email'
];

// Check if a field should skip sanitization
const shouldSkipSanitization = (key) => {
  return NO_SANITIZE_FIELDS.includes(key);
};

// Sanitize string inputs to prevent XSS while preserving special characters for technical fields
const sanitizeString = (str, fieldName = '') => {
  if (typeof str !== 'string') return str;
  
  // Skip sanitization for technical fields that need special characters
  if (shouldSkipSanitization(fieldName)) {
    return str.trim();
  }
  
  // For other fields, only remove script tags and dangerous HTML
  return DOMPurify.sanitize(str, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
    KEEP_CONTENT: true
  }).trim();
};

// Recursively sanitize objects
const sanitizeObject = (obj, parentKey = '') => {
  if (obj === null || obj === undefined) return obj;
  
  if (Array.isArray(obj)) {
    return obj.map((item, index) => sanitizeObject(item, `${parentKey}[${index}]`));
  }
  
  if (typeof obj === 'object') {
    const sanitized = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const fullKey = parentKey ? `${parentKey}.${key}` : key;
        sanitized[key] = sanitizeObject(obj[key], key);
      }
    }
    return sanitized;
  }
  
  if (typeof obj === 'string') {
    return sanitizeString(obj, parentKey);
  }
  
  return obj;
};

// ========== INSTALLATION APIs ==========
export const createInstallation = async (data) => {
  const response = await api.post('/installation', data);
  return response.data;
};

export const getAllInstallations = async (page = 1, limit = 10) => {
  const response = await api.get('/installations', { params: { page, limit } });
  return response.data;
};

export const getInstallationByUniqueId = async (uniqueId) => {
  const response = await api.get(`/installation/${uniqueId}/configs`);
  return response.data;
};

export const updateInstallation = async (uniqueId, data) => {
  const response = await api.put(`/installation/${uniqueId}`, data);
  return response.data;
};

export const deleteInstallation = async (uniqueId) => {
  const response = await api.delete(`/installation/${uniqueId}`);
  return response.data;
};

// ========== BACKUP CONFIG APIs ==========
export const createBackupConfig = async (uniqueId, data) => {
  const response = await api.post(`/installation/${uniqueId}/backup-config`, data);
  return response.data;
};

export const getBackupConfigsByInstallation = async (uniqueId) => {
  const response = await api.get(`/installation/${uniqueId}/backup-configs`);
  return response.data;
};

export const getAllBackupConfigs = async (page = 1, limit = 10) => {
  const response = await api.get('/backup-configs', { params: { page, limit } });
  return response.data;
};

export const updateBackupConfig = async (configId, data) => {
  const response = await api.put(`/backup-config/${configId}`, data);
  return response.data;
};

export const deleteBackupConfig = async (configId) => {
  const response = await api.delete(`/backup-config/${configId}`);
  return response.data;
};

// ========== BACKUP LOG APIs ==========
export const getBackupLogsByInstallation = async (uniqueId, page = 1, limit = 10) => {
  const response = await api.get(`/installation/${uniqueId}/backup-logs`, {
    params: { page, limit }
  });
  return response.data;
};

export const getAllBackupLogs = async (page = 1, limit = 10) => {
  const response = await api.get('/backup-logs', { params: { page, limit } });
  return response.data;
};

export const getBackupLogById = async (logId) => {
  const response = await api.get(`/backup-log/${logId}`);
  return response.data;
};

// ========== SCHEDULE MASTER APIs ==========
export const createScheduleMaster = async (data) => {
  const response = await api.post('/schedule-master', data);
  return response.data;
};

export const getAllScheduleMasters = async (page = 1, limit = 100) => {
  const response = await api.get('/schedule-masters', { params: { page, limit } });
  return response.data;
};

export const getScheduleMasterById = async (scheduleId) => {
  const response = await api.get(`/schedule-master/${scheduleId}`);
  return response.data;
};

export const updateScheduleMaster = async (scheduleId, data) => {
  const response = await api.put(`/schedule-master/${scheduleId}`, data);
  return response.data;
};

export const deleteScheduleMaster = async (scheduleId) => {
  const response = await api.delete(`/schedule-master/${scheduleId}`);
  return response.data;
};

// ========== SFTP SERVER MASTER APIs ==========
export const createSftpServerMaster = async (data) => {
  const response = await api.post('/sftp-server-master', data);
  return response.data;
};

export const getAllSftpServerMasters = async (page = 1, limit = 100) => {
  const response = await api.get('/sftp-server-masters', { params: { page, limit } });
  return response.data;
};

export const getSftpServerMasterById = async (serverId) => {
  const response = await api.get(`/sftp-server-master/${serverId}`);
  return response.data;
};

export const updateSftpServerMaster = async (serverId, data) => {
  const response = await api.put(`/sftp-server-master/${serverId}`, data);
  return response.data;
};

export const deleteSftpServerMaster = async (serverId) => {
  const response = await api.delete(`/sftp-server-master/${serverId}`);
  return response.data;
};

// ========== STATISTICS APIs ==========
export const getStatistics = async () => {
  const response = await api.get('/stats');
  return response.data;
};

export default api;